import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { GHARS, GHAR_TYPE_HI, WEEKDAYS_HI, resolvePreacherHi, resolvePathiHi, resolveSpecialHi, satsangTime, cellTime, defaultHM, fmt12 } from "../data/rssbData";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Clock, ShieldCheck, AlertTriangle } from "lucide-react";
import { findClashes } from "../lib/clashes";
import { Maximize2, Minimize2 } from "lucide-react";

const typeStyle = {
  SK: "bg-[#f7e9e4] text-[#5e1414] border-[#d9b8a8]",
  SR: "bg-[#fffdf7] text-[#3a1212] border-[#e3d6b6]",
  CD: "bg-[#efe4ca] text-[#7a4a3a] border-[#d9c9a3]",
  LCD: "bg-[#efe4ca] text-[#7a4a3a] border-[#d9c9a3]",
  SPECIAL: "bg-gradient-to-b from-[#6e1b1b] to-[#431010] text-[#f3ead6] border-[#431010]",
};

const TimeTag = ({ cell, iso }) => (
  <div
    data-testid={`cell-time-${iso}`}
    className={`inline-block text-[11px] font-semibold px-1.5 rounded mb-1 ${cell.time ? "bg-[#b8860b] text-white" : "bg-black/10"}`}
    title={cell.time ? "बदला हुआ समय" : "सामान्य समय"}
  >
    {cellTime(cell, iso)}
  </div>
);

const CellContent = ({ cell, iso }) => {
  if (!cell) return <span className="text-[#cdb98f]">×</span>;
  if (cell.type === "CD" || cell.type === "LCD")
    return (
      <div>
        <TimeTag cell={cell} iso={iso} />
        <div className="italic font-semibold">{cell.type === "CD" ? "सी डी सत्संग" : "सवाल-जवाब सत्संग"}</div>
      </div>
    );
  const top = cell.type === "SPECIAL" ? resolveSpecialHi(cell, iso, false) : resolvePreacherHi(cell);
  return (
    <div className="space-y-1.5 leading-snug">
      <TimeTag cell={cell} iso={iso} />
      <div className="font-bold">{top || "—"}</div>
      <div className="pt-1.5 border-t border-current/20 border-dashed opacity-90">
        <span className="text-[11px] opacity-70">पाठी: </span>
        {resolvePathiHi(cell) || "—"}
      </div>
    </div>
  );
};

// Date header: change the time of every satsang on that day at once.
const DayTime = ({ d, dayHM, onDayTime }) => {
  const [open, setOpen] = React.useState(false);
  const base = defaultHM(d.iso, "morning", d.dow);
  const [hm, setHm] = React.useState(dayHM || base);
  const apply = (v) => {
    onDayTime(d.iso, v);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setHm(dayHM || base); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`day-time-btn-${d.iso}`}
          className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold ${dayHM ? "bg-[#b8860b] text-white" : "bg-white/15 hover:bg-white/25"}`}
        >
          <Clock className="w-3 h-3" /> {fmt12(dayHM || base)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 bg-[#fffdf7] border-[#cdb98f] font-dev" data-testid={`day-time-pop-${d.iso}`}>
        <div className="text-sm font-bold text-[#5a2a24]">{d.iso.split("-").reverse().join("/")} — सभी सत्संगों का समय</div>
        <input
          type="time"
          value={hm}
          onChange={(e) => setHm(e.target.value)}
          data-testid={`day-time-input-${d.iso}`}
          className="mt-2 w-full rounded-md border border-[#e3d6b6] bg-[#f6eeda] px-2 py-1.5 text-[#3a1212]"
        />
        <p className="mt-1 text-[11px] text-[#7a4a3a]">इस दिन के सभी सत्संगों पर लागू होगा (विशेष SK का दोपहर सत्संग छोड़कर)। बाद में किसी एक सत्संग का समय अलग से भी बदल सकते हैं।</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button type="button" data-testid={`day-time-reset-${d.iso}`} onClick={() => apply(null)} className="text-xs underline text-[#7a4a3a]">
            सामान्य समय ({fmt12(base)})
          </button>
          <button type="button" data-testid={`day-time-apply-${d.iso}`} onClick={() => apply(hm)} className="rounded-md bg-[#6e1b1b] px-3 py-1.5 text-sm text-white">
            लागू करें
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ClashPanel = ({ clashes }) => (
  <div
    data-testid="clash-panel"
    className={`rounded-lg border px-4 py-3 font-dev text-sm ${clashes.list.length ? "border-red-300 bg-red-50 text-red-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}
  >
    {clashes.list.length ? (
      <>
        <div className="font-bold flex items-center gap-1.5" data-testid="clash-count">
          <AlertTriangle className="w-4 h-4" /> {clashes.list.length} टकराव मिले — एक ही समय पर एक सेवादार कई जगह
        </div>
        <ul className="mt-1.5 space-y-0.5">
          {clashes.list.map((c, i) => (
            <li key={i} data-testid={`clash-item-${i}`}>
              {c.iso.split("-").reverse().join("/")} · {c.time} · <b>{c.name}</b> → {c.ghars.join(", ")}
            </li>
          ))}
        </ul>
      </>
    ) : (
      <div className="font-bold flex items-center gap-1.5" data-testid="clash-none">
        <ShieldCheck className="w-4 h-4" /> कोई टकराव नहीं — किसी सेवादार की एक समय पर दो जगह सेवा नहीं है
      </div>
    )}
  </div>
);

const GridTable = ({ variant, dates, onCellClick, onDayTime, clashSet }) => (
  <table className="border-collapse text-[13px] font-dev min-w-max">
    <thead className="sticky top-0 z-20">
      <tr className="bg-[#5e1414] text-[#f3ead6]">
        <th className="sticky left-0 z-30 bg-[#5e1414] px-2 py-2.5 border border-[#7a1f1f] w-12">क्र.</th>
        <th className="sticky left-12 z-30 bg-[#5e1414] px-3 py-2.5 border border-[#7a1f1f] text-left min-w-[110px] sm:min-w-[170px]">सत्संग जगह</th>
        {dates.map((d) => (
          <th key={d.iso} className="px-3 py-2 border border-[#7a1f1f] min-w-[180px]">
            <div className="font-bold">{d.iso.split("-").reverse().join("/")}</div>
            <div className="text-xs font-normal opacity-90">{WEEKDAYS_HI[d.dow]}</div>
            {onDayTime ? <DayTime d={d} dayHM={variant.dayTimes?.[d.iso]} onDayTime={onDayTime} /> : <div className="text-xs">{satsangTime(d.iso, d.dow)}</div>}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {GHARS.map((g, gi) => (
        <tr key={g.id} className={gi % 2 ? "bg-[#f6eeda]" : "bg-[#fffdf7]"}>
          <td className="sticky left-0 z-10 bg-inherit px-2 py-3 border border-[#e3d6b6] text-center text-[#7a4a3a]">{gi + 1}</td>
          <td className="sticky left-12 z-10 bg-inherit px-3 py-3 border border-[#e3d6b6] text-[#3a1212] font-semibold min-w-[110px] sm:min-w-[170px]">
            {g.hi}
            <span className="text-[#6e1b1b] text-xs font-bold">–{GHAR_TYPE_HI[g.type]}</span>
          </td>
          {dates.map((d) => {
            const cell = variant?.cells?.[g.id]?.[d.iso];
            const active = g.days.includes(d.dow);
            const clash = clashSet?.has(`${g.id}|${d.iso}`);
            return (
              <td
                key={d.iso}
                data-testid={`cell-${g.id}-${d.iso}`}
                onClick={() => onCellClick(g, d, cell || null)}
                title={cell ? "संपादित करें" : "सत्संग जोड़ें (क्लिक करें)"}
                data-clash={clash ? "true" : undefined}
                className={`grid-cell border px-3 py-2.5 align-top cursor-pointer max-w-[220px] ${clash ? "!bg-red-100 outline outline-2 -outline-offset-2 outline-red-600 " : ""}${
                  cell ? typeStyle[cell.type] || "bg-[#fffdf7] border-[#e3d6b6]" : `text-center border-[#e3d6b6] ${active ? "bg-[#fffdf7]" : "bg-[#f1e6cf]"}`
                }`}
              >
                {clash && <div className="text-[10px] font-bold text-red-700 mb-0.5">⚠ टकराव</div>}
                {cell ? <CellContent cell={cell} iso={d.iso} /> : <span className="text-[#6e1b1b] opacity-20 hover:opacity-100 select-none">+</span>}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  </table>
);

export const ScheduleGrid = (props) => {
  const [full, setFull] = useState(false);
  const [check, setCheck] = useState(false);
  const clashes = React.useMemo(() => (check ? findClashes(props.variant, props.dates) : null), [check, props.variant, props.dates]);
  const checkBtn = (
    <button
      data-testid="recheck-schedule-btn"
      onClick={() => setCheck((c) => !c)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold font-dev transition-colors ${check ? "bg-[#b8860b] text-white" : "bg-[#fffdf7] text-[#6e1b1b] border border-[#6e1b1b]"}`}
    >
      <ShieldCheck className="w-4 h-4" /> {check ? "जाँच बंद करें" : "शेड्यूल दोबारा जाँचें"}
    </button>
  );
  const gridProps = { ...props, clashSet: clashes?.cells };
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => e.key === "Escape" && !document.querySelector('[role="dialog"]') && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  const toggle = (
    <button
      data-testid="grid-fullscreen-toggle"
      onClick={() => setFull((f) => !f)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold font-dev bg-[#6e1b1b] text-[#f3ead6] hover:bg-[#7a1f1f] transition-colors"
    >
      {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      {full ? "पूर्ण स्क्रीन बंद करें" : "पूर्ण स्क्रीन में खोलें"}
    </button>
  );
  if (full)
    return createPortal(
      <div className="fixed inset-0 z-[45] bg-[#f3ead6] flex flex-col" data-testid="schedule-grid-full">
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-[#5e1414] text-[#f3ead6]">
          <span className="font-serif-dev font-semibold">पूर्ण शेड्यूल — सेल पर क्लिक कर बदलें</span>
          <div className="flex gap-2">{checkBtn}{toggle}</div>
        </div>
        {clashes && <div className="px-4 pt-2 max-h-40 overflow-auto"><ClashPanel clashes={clashes} /></div>}
        <div className="flex-1 overflow-auto" data-testid="schedule-grid">
          <GridTable {...gridProps} />
        </div>
      </div>,
      document.body
    );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap justify-end gap-2">{checkBtn}{toggle}</div>
      {clashes && <ClashPanel clashes={clashes} />}
      <div className="rounded-xl border border-[#cdb98f] bg-[#fffdf7] shadow-[0_12px_30px_rgba(94,20,20,0.18)] overflow-auto max-h-[80vh]" data-testid="schedule-grid">
        <GridTable {...gridProps} />
      </div>
    </div>
  );
};
